import { useEffect, useRef, useState } from "react";
import { executeDimensionEntries } from "../../lib/apiClient.js";
import { dashboardScopeToApiScope } from "../../lib/analyticsClient.js";
import { listMemoryBodies } from "../../lib/memories.js";
import { parseIndexEntries } from "../../lib/memoryGraph.js";

const EMPTY_SET = new Set();
const EMPTY_MAP = new Map();
const MAX_HYDRATE = 48; // cap leaf bodies to distinct memories
const INDEX_PROBE = 30; // recent MEMORY index versions to walk (early-stops at trackSlug)

// Data behind the memory-store graph for one scope. Three tiers, published as
// each resolves so the UI never blocks on the slowest:
//   • capturedSlugs — fast (one group-by, the exact set the sidebar lists);
//     drives link liveness, so links resolve before any hydration.
//   • indexBody     — the current MEMORY.md content. The single most-recent touch
//     is usually an `Edit` (no full body), so we walk recent MEMORY events until
//     one carries a Read/Write body. Used by LEAF views (which don't already hold
//     the index text) for backlinks/orphan status.
//   • bodies        — when `deep`, the latest body per memory (bounded
//     hydration), for leaf↔leaf wikilink edges.
// Everything degrades: a failed query/hydration just yields fewer edges, never a
// throw. Keyed on (scope, range, deep); switching memory VERSIONS doesn't refetch
// because the viewed text is passed in by the caller, not fetched here.
//
// SCOPE: capturedSlugs goes through executeDimensionEntries (which translates the
// dashboard scope internally). The memory queries here take an already-API-form
// scope, so we translate via dashboardScopeToApiScope first — passing the raw
// plural-dim scope makes buildPromptRootAndFilter match nothing and silently run
// UNSCOPED across every endpoint's memory files.
export function useMemoryStore({ ancestorScope = [], range, deep = false, trackSlug = null } = {}) {
  const [state, setState] = useState({
    capturedSlugs: EMPTY_SET,
    capturedLoading: true,
    indexBody: null,
    indexVersions: [],
    bodies: EMPTY_MAP,
    deepLoading: true,
  });
  const scopeKey = JSON.stringify(ancestorScope);
  const runId = useRef(0);

  useEffect(() => {
    const token = ++runId.current;
    let cancelled = false;
    const live = () => !cancelled && runId.current === token;
    setState({ capturedSlugs: EMPTY_SET, capturedLoading: true, indexBody: null, indexVersions: [], bodies: EMPTY_MAP, deepLoading: true });

    (async () => {
      // 1. Captured set — fast; publish immediately for link liveness.
      let capturedSlugs = EMPTY_SET;
      try {
        const res = await executeDimensionEntries({ dimKey: "memories", parentScopePath: ancestorScope, range, limit: 1000 });
        capturedSlugs = new Set((res?.entries || []).map((e) => e.id));
      } catch { /* keep empty */ }
      if (!live()) return;
      setState((s) => ({ ...s, capturedSlugs, capturedLoading: false }));

      const { apiScope } = dashboardScopeToApiScope(ancestorScope);

      // 2. Index HISTORY (LEAF views only) — the MEMORY index body over time from
      // the `memory` signal (tag "MEMORY", newest-first). Each full-content
      // (write/read) version → its listed slugs; publish the newest body first
      // (backlinks/orphan resolve fast), then stop once `trackSlug` appears
      // (referenced-now, or last-listed before it was dropped). NB: the index tag
      // is the bare "MEMORY" — a multi-project endpoint's per-project indexes
      // aren't separable from the tag alone (project lives in the value); exact for
      // the common one-project endpoint.
      let indexBody = null;
      const indexVersions = [];
      if (trackSlug) {
        try {
          const rows = await listMemoryBodies({ apiScope, range, slug: "MEMORY", limit: INDEX_PROBE });
          for (const { body, op, ts } of rows) {
            if (!live()) return;
            if ((op !== "write" && op !== "read") || !body) continue;
            const slugs = parseIndexEntries(body).map((e) => e.slug);
            indexVersions.push({ ts, slugs });
            if (!indexBody) { indexBody = body; setState((s) => ({ ...s, indexBody })); }
            if (slugs.includes(trackSlug)) break; // referenced-now or last-listed — answered
          }
        } catch { /* no index body */ }
        if (!live()) return;
        setState((s) => ({ ...s, indexVersions }));
      }

      // 3. Deep: per-leaf latest body for wikilink edges (index map view only). One
      // signal read; keep the newest full-content body per slug (skip the index +
      // partial edits), bounded to MAX_HYDRATE distinct memories.
      if (!deep) { setState((s) => ({ ...s, deepLoading: false })); return; }
      let rows = [];
      try { rows = await listMemoryBodies({ apiScope, range, limit: 1000 }); } catch { /* empty */ }
      if (!live()) return;
      const bodies = new Map();
      for (const { slug, body, op } of rows) { // newest-first
        if (!slug || slug === "MEMORY" || op === "edit" || !body) continue;
        if (bodies.has(slug)) continue;        // first (newest) full body wins
        if (bodies.size >= MAX_HYDRATE) continue;
        bodies.set(slug, body);
      }
      setState((s) => ({ ...s, bodies: new Map(bodies), deepLoading: false }));
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, range, deep]);

  return state;
}
